import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faScaleBalanced,
  faHome,
  faChartPie,
  faBriefcase,
  faPlus,
  faCog,
  faUser,
  faChevronDown,
  faUserCog,
  faSignOutAlt,
  faSmile,
  faBug,
  faSearch,
  faSpinner,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useGlobalSearch } from '../../hooks/useCases';
import { useFirmLogo } from '../../hooks/useAdmin';
import ReportIssueModal from './ReportIssueModal';
import DailyJokeModal from '../../pages/HomePage/components/DailyJokeModal';
import { BRAND_NAME } from '../../constants';
import styles from './Header.module.css';

const NAV_ITEMS = [
  { key: 'home', icon: faHome, label: 'Home', to: '/' },
  { key: 'dashboard', icon: faChartPie, label: 'Dashboard', to: '/dashboard' },
  { key: 'cases', icon: faBriefcase, label: 'Cases', to: '/cases' },
  { key: 'tasks', icon: faListCheck, label: 'Tasks', to: '/tasks' },
  { key: 'new-record', icon: faPlus, label: 'New Record', to: '/new-record' },
  { key: 'admin', icon: faCog, label: 'Admin', to: '/admin' },
] as const;

interface Props {
  subtitle?: string;
}

export default function Header({ subtitle = '' }: Props) {
  const { user, logout } = useAuth();
  const { canAccessAdmin, isSuperAdmin, hasPermission } = usePermissions();
  const canAccessDashboard = isSuperAdmin || hasPermission('dashboard_access');
  const { data: headerLogo } = useFirmLogo('header');
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [showJoke, setShowJoke] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /* ── Global search state ── */
  const {
    results: searchResults,
    searching,
    search: globalSearch,
    clear: clearSearch,
  } = useGlobalSearch();
  const [globalTerm, setGlobalTerm] = useState('');
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close search overlay on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setShowSearchOverlay(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleGlobalInput = (term: string) => {
    setGlobalTerm(term);
    if (term.length >= 2) {
      setShowSearchOverlay(true);
      globalSearch(term);
    } else {
      setShowSearchOverlay(false);
      clearSearch();
    }
  };

  function activeKey(): string {
    const p = location.pathname;
    if (p === '/') return 'home';
    if (p.startsWith('/dashboard')) return 'dashboard';
    if (p === '/new-record') return 'new-record';
    if (p.startsWith('/contacts')) return 'cases';
    if (p.startsWith('/cases')) return 'cases';
    if (p.startsWith('/admin')) return 'admin';
    return '';
  }
  const active = activeKey();

  return (
    <header className={styles.header}>
      <div className={styles.headerTop}>
        {/* Brand */}
        <Link to="/" className={styles.brand}>
          <div className={styles.brandLogo}>
            {headerLogo?.url ? (
              <img src={headerLogo.url} alt="Firm logo" className={styles.brandLogoImg} />
            ) : (
              <FontAwesomeIcon icon={faScaleBalanced} />
            )}
          </div>
          <div>
            <h1 className={styles.brandTitle}>{BRAND_NAME}</h1>
            {subtitle && <span className={styles.brandSubtitle}>{subtitle}</span>}
          </div>
        </Link>

        {/* Nav */}
        <nav className={styles.nav}>
          {NAV_ITEMS.filter((n) => {
            if (n.key === 'admin') return canAccessAdmin;
            if (n.key === 'dashboard') return canAccessDashboard;
            return true;
          }).map((n) => (
            <Link
              key={n.key}
              to={n.to}
              className={`${styles.navItem} ${active === n.key ? styles.navItemActive : ''}`}
            >
              <FontAwesomeIcon icon={n.icon} />
              <span>{n.label}</span>
            </Link>
          ))}
          <button
            className={`${styles.navItem} ${styles.reportIssue}`}
            onClick={() => setShowReportIssue(true)}
            type="button"
            title="Report Issue"
          >
            <FontAwesomeIcon icon={faBug} />
            <span>Report Issue</span>
          </button>
        </nav>

        {/* User */}
        <div className={styles.userArea} ref={dropdownRef}>
          <button className={styles.userButton} onClick={() => setOpen((v) => !v)} type="button">
            <div className={styles.avatar}>
              <FontAwesomeIcon icon={faUser} />
            </div>
            <span>{user?.username ?? 'User'}</span>
            <FontAwesomeIcon icon={faChevronDown} size="xs" />
          </button>

          {open && (
            <div className={styles.dropdown}>
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setOpen(false);
                  setShowJoke(true);
                }}
                type="button"
              >
                <FontAwesomeIcon icon={faSmile} />
                <span>Daily Joke</span>
              </button>
              <Link
                to="/preferences"
                className={styles.dropdownItem}
                onClick={() => setOpen(false)}
              >
                <FontAwesomeIcon icon={faUserCog} />
                <span>Preferences</span>
              </Link>
              <button
                className={`${styles.dropdownItem} ${styles.dropdownDanger}`}
                onClick={logout}
                type="button"
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Global search — second row */}
      <div className={styles.globalSearch} ref={searchWrapRef}>
        <div className={styles.searchInputWrap}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search contacts by name, email, or case ref…"
            value={globalTerm}
            onChange={(e) => handleGlobalInput(e.target.value)}
            onFocus={() => {
              if (searchResults.length > 0) setShowSearchOverlay(true);
            }}
          />
          {searching && <FontAwesomeIcon icon={faSpinner} spin className={styles.searchSpinner} />}
        </div>

        {showSearchOverlay && (searchResults.length > 0 || searching) && (
          <div className={styles.searchOverlay}>
            {searchResults.length === 0 && searching && (
              <div className={styles.searchNoResults}>Searching…</div>
            )}
            {searchResults.length === 0 && !searching && (
              <div className={styles.searchNoResults}>No contacts found</div>
            )}
            {searchResults.map((r) => {
              const id = r.ContactID || r.id;
              const name =
                r.name || [r.first_name, r.last_name].filter(Boolean).join(' ') || 'Unknown';
              return (
                <div
                  key={id}
                  className={styles.searchResultItem}
                  onClick={() => {
                    if (id) navigate(`/contacts/${id}`);
                    setShowSearchOverlay(false);
                    clearSearch();
                    setGlobalTerm('');
                  }}
                >
                  <div className={styles.searchResultName}>{name}</div>
                  {r.email && <div className={styles.searchResultMeta}>{r.email}</div>}
                  {r.caseRefs && <div className={styles.searchResultMeta}>Cases: {r.caseRefs}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showReportIssue && <ReportIssueModal onClose={() => setShowReportIssue(false)} />}
      {showJoke && <DailyJokeModal onClose={() => setShowJoke(false)} />}
    </header>
  );
}
